import React, { useState } from 'react';
import { DownloadIcon, SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../../contexts/StoreContext';
import { Modal } from '../ui/Modal';
import { LeadProgress } from '../credit/StatusTracker';
import { channelLabels, leadStatusSteps } from '../../data/options';
import { Lead, LeadChannel, LeadStatus } from '../../types/lead';
import { formatARS, formatDateTime, formatPct } from '../../utils/format';
import { amortizationLabel } from '../../utils/finance';
const statusLabel = (s: LeadStatus) => leadStatusSteps.find(x => x.id === s)?.label ?? s;
const statusColor: Record<LeadStatus, string> = {
  recibida: 'text-neon border-neon/30',
  'en-revision': 'text-warn border-warn/30',
  derivada: 'text-[#C4B5FD] border-[#C4B5FD]/30',
  contactada: 'text-ok border-ok/30',
  cerrada: 'text-mist border-line'
};
function exportCsv(leads: Lead[]) {
  const cols = ['codigo', 'fecha', 'canal', 'nombre', 'telefono', 'email', 'medio', 'vehiculo', 'precio', 'anticipo', 'plazo', 'tna', 'cuota', 'condicion', 'anticipo_disponible', 'situacion_laboral', 'consentimiento_datos', 'consentimiento_contacto', 'estado', 'asesor', 'banco', 'cuil', 'usado'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = leads.map(l => [l.code, l.createdAt, channelLabels[l.channel], l.name, l.phone, l.email, l.contactPref, l.vehicleLabel, l.price, l.down, l.term, l.simulation?.tna, l.simulation ? Math.round(l.simulation.installment) : '', l.simulation ? l.simulation.verified ? 'verificada' : 'referencial' : '', l.credit?.downRange, l.credit?.employment, l.consents.dataProcessing ? 'si' : 'no', l.consents.contact ? 'si' : 'no', statusLabel(l.status), l.assignedTo, l.bank, l.cuil, l.tradeIn ? `${l.tradeIn.brand} ${l.tradeIn.model} ${l.tradeIn.year} ${l.tradeIn.km}km` : ''].map(esc).join(','));
  const blob = new Blob([`\uFEFF${cols.join(',')}\n${rows.join('\n')}`], {
    type: 'text/csv;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vcars-consultas-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
function LeadDetail({
  lead,
  onClose
}: {
  lead: Lead;
  onClose: () => void;
}) {
  const {
    updateLead,
    deleteLead
  } = useStore();
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [assigned, setAssigned] = useState(lead.assignedTo);
  const [note, setNote] = useState('');
  const info: [string, string][] = [['Código', lead.code], ['Fecha', formatDateTime(lead.createdAt)], ['Canal de origen', channelLabels[lead.channel]], ['Teléfono', lead.phone || '—'], ['Email', lead.email || '—'], ['Medio preferido', lead.contactPref], ['Vehículo', lead.vehicleLabel ?? '—'], ['Precio consultado', lead.price ? formatARS(lead.price) : '—'], ['Anticipo', lead.down ? formatARS(lead.down) : '—'], ['Plazo', lead.term ? `${lead.term} meses` : '—']];
  if (lead.simulation) {
    info.push(['Simulación', `${lead.simulation.term} × ${formatARS(lead.simulation.installment)} · ${amortizationLabel(lead.simulation.amortization)}`], ['TNA / CFT', `${formatPct(lead.simulation.tna)} / ${formatPct(lead.simulation.cft)}`], ['Costo total estimado', formatARS(lead.simulation.totalCost)], ['Tipo de condición', lead.simulation.verified ? 'Oferta verificada' : 'Simulación referencial']);
  }
  if (lead.bank) info.push(['Banco', lead.bank]);
  if (lead.cuil) info.push(['CUIL', `${lead.cuil.slice(0, 2)}-${lead.cuil.slice(2, 10)}-${lead.cuil.slice(10)}`]);
  if (lead.tradeIn) {
    info.push(['Usado', `${lead.tradeIn.brand} ${lead.tradeIn.model} · ${lead.tradeIn.year} · ${lead.tradeIn.km.toLocaleString('es-AR')} km`]);
  }
  if (lead.credit) {
    info.push(['Anticipo disponible', lead.credit.downRange], ['Situación laboral', lead.credit.employment], ['Antigüedad', lead.credit.seniority]);
  }
  info.push(['Consentimiento de datos', lead.consents.dataProcessing ? `Otorgado · ${formatDateTime(lead.consents.at)}` : 'No otorgado'], ['Consentimiento de contacto', lead.consents.contact ? 'Otorgado' : 'No otorgado']);
  return <div className="space-y-6" data-id="element-122" data-mp="5tkse82ot7w">
      <div data-id="element-123" data-mp="52k192nshqtu">
        <p className="font-display text-xl font-bold" data-id="element-124" data-mp="52ahhwtc8fwl">{lead.name}</p>
        {lead.message && <p className="mt-2 rounded-lg bg-panel p-3 text-sm text-mist" data-id="element-125" data-mp="3mjjynroomf4o">{lead.message}</p>}
      </div>
      <dl className="divide-y divide-line text-sm" data-id="element-126" data-mp="3r9s2bsrofbk2">
        {info.map(([k, v]) => <div key={k} className="grid grid-cols-[150px_1fr] gap-3 py-2" data-id="element-127" data-mp="3dilvtcpzcdno">
            <dt className="text-mist" data-id="element-128" data-mp="3dgiu3vfgsw34">{k}</dt>
            <dd className="break-words" data-id="element-129" data-mp="3ih6w1c58exc4">{v}</dd>
          </div>)}
      </dl>
      <div className="grid gap-3 rounded-xl border border-line p-4 sm:grid-cols-2" data-id="element-130" data-mp="33kpkokx1lipk">
        <div data-id="element-131" data-mp="33msap4y73bua">
          <label htmlFor="ld-status" className="label" data-id="element-132" data-mp="38j99qu6xnam5">Estado comercial</label>
          <select id="ld-status" className="input" value={status} onChange={e => setStatus(e.target.value as LeadStatus)} data-id="element-133" data-mp="38ldbb5hczxj9">
            {leadStatusSteps.map(s => <option key={s.id} value={s.id} data-id="element-134" data-mp="2kx6whnhixmp8">{s.label}</option>)}
          </select>
        </div>
        <div data-id="element-135" data-mp="2ywy6ze8u0kyh">
          <label htmlFor="ld-assign" className="label" data-id="element-136" data-mp="2yxhrn2pv9ng7">Asesor asignado</label>
          <input id="ld-assign" className="input" value={assigned} onChange={e => setAssigned(e.target.value)} maxLength={60} data-id="element-137" data-mp="2twk7iklaqjc2" />
        </div>
        <div className="sm:col-span-2" data-id="element-138" data-mp="37uibqx5wkzdu">
          <label htmlFor="ld-note" className="label" data-id="element-139" data-mp="33vjbvqz13st9">Nota interna</label>
          <input id="ld-note" className="input" value={note} onChange={e => setNote(e.target.value)} maxLength={200} placeholder="Ej.: Se envió cotización por WhatsApp" data-id="element-140" data-mp="33t6r761vz4ci" />
        </div>
        <button className="btn btn-primary sm:col-span-2" onClick={() => {
        updateLead(lead.id, {
          status,
          note,
          assignedTo: assigned
        });
        setNote('');
        toast.success('Consulta actualizada');
      }} data-id="element-141" data-mp="3cpedwq4ut34o">
          Guardar seguimiento
        </button>
      </div>
      <div data-id="element-142" data-mp="2i4qregbs85w">
        <h3 className="mb-3 text-sm font-semibold" data-id="element-143" data-mp="2hv0rqva6107">Seguimiento</h3>
        <LeadProgress lead={lead} data-id="element-144" data-mp="2g1ptzdmwume" />
      </div>
      <button className="btn btn-danger w-full" onClick={() => {
      if (window.confirm('¿Eliminar esta consulta y sus datos personales?')) {
        deleteLead(lead.id);
        onClose();
        toast.success('Consulta eliminada');
      }
    }} data-id="element-145" data-mp="7gg0lp6meio4">
        Eliminar consulta (derecho de supresión)
      </button>
    </div>;
}
export function LeadsPanel() {
  const {
    leads
  } = useStore();
  const [status, setStatus] = useState<LeadStatus | ''>('');
  const [channel, setChannel] = useState<LeadChannel | ''>('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const list = leads.filter(l => (!status || l.status === status) && (!channel || l.channel === channel) && `${l.name} ${l.code} ${l.phone} ${l.vehicleLabel ?? ''}`.toLowerCase().includes(q.toLowerCase()));
  const open = leads.find(l => l.id === openId) ?? null;
  const counts = leadStatusSteps.map(s => ({
    ...s,
    n: leads.filter(l => l.status === s.id).length
  }));
  return <div data-id="element-146" data-mp="2aqjcdb39vrx0">
      <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-line pb-5" data-id="element-147" data-mp="2aqstsaa94qr3">
        {counts.map(c => <button key={c.id} onClick={() => setStatus(status === c.id ? '' : c.id)} className={`text-left ${status === c.id ? 'text-white' : 'text-mist hover:text-white'}`} data-id="element-148" data-mp="38cqduc4a5455">
            <span className="block font-display text-2xl font-bold" data-id="element-149" data-mp="2yf2jvoei00o1">{c.n}</span>
            <span className="text-xs" data-id="element-150" data-mp="2tgiwrko64iu3">{c.label}</span>
          </button>)}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_200px_200px_auto]" data-id="element-151" data-mp="1n0f9x71tly7l">
        <div className="relative" data-id="element-152" data-mp="1n1qa6ww24nty">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mist" aria-hidden data-id="element-153" data-mp="1i1lsx4nbrp2o" />
          <label htmlFor="ld-q" className="sr-only" data-id="element-154" data-mp="27og6m7vkw7rz">Buscar</label>
          <input id="ld-q" className="input pl-9" placeholder="Nombre, código, teléfono o vehículo" value={q} onChange={e => setQ(e.target.value)} data-id="element-155" data-mp="2coaxwcj9mzdk" />
        </div>
        <select aria-label="Estado" className="input" value={status} onChange={e => setStatus(e.target.value as LeadStatus | '')} data-id="element-156" data-mp="1yqmaoy1q7g5c">
          <option value="" data-id="element-157" data-mp="2qg90e4ih1x0r">Todos los estados</option>
          {leadStatusSteps.map(s => <option key={s.id} value={s.id} data-id="element-158" data-mp="2qibouk3z7i3d">{s.label}</option>)}
        </select>
        <select aria-label="Canal" className="input" value={channel} onChange={e => setChannel(e.target.value as LeadChannel | '')} data-id="element-159" data-mp="2veaj2obzjubp">
          <option value="" data-id="element-160" data-mp="2lp914slvr2v3">Todos los canales</option>
          {(Object.keys(channelLabels) as LeadChannel[]).map(c => <option key={c} value={c} data-id="element-161" data-mp="39ebj61kfexq4">{channelLabels[c]}</option>)}
        </select>
        <button onClick={() => exportCsv(list)} disabled={!list.length} className="btn btn-ghost" data-id="element-162" data-mp="3e5js6whugh2r">
          <DownloadIcon className="h-4 w-4" aria-hidden data-id="element-163" data-mp="12qkz27s76ygq" /> CSV
        </button>
      </div>

      {list.length === 0 ? <div className="mt-6 rounded-2xl border border-dashed border-line p-10 text-center text-sm text-mist" data-id="element-164" data-mp="xs1d47dltmod">
          {leads.length === 0 ? 'Todavía no hay consultas. Aparecerán acá cuando los clientes completen un formulario o consulten por WhatsApp.' : 'No hay consultas con esos filtros.'}
        </div> : <ul className="mt-5 divide-y divide-line rounded-2xl border border-line" data-id="element-165" data-mp="jsry3b14wylz">
          {list.map(l => <li key={l.id} data-id="element-166" data-mp="eu6t6yaxtqr7">
              <button onClick={() => setOpenId(l.id)} className="grid w-full gap-2 p-4 text-left transition-colors duration-150 hover:bg-white/[0.03] sm:grid-cols-[1.2fr_1.5fr_1fr_auto] sm:items-center sm:gap-4" data-id="element-167" data-mp="ev0328570wdk">
                <span data-id="element-168" data-mp="2551r8r259es7">
                  <span className="block font-semibold" data-id="element-169" data-mp="254a506ufstel">{l.name}</span>
                  <span className="font-mono text-xs text-mist" data-id="element-170" data-mp="205ystpgo66t7">{l.code}</span>
                </span>
                <span className="text-sm text-mist" data-id="element-171" data-mp="jripsd2vfzs8">{l.vehicleLabel ?? 'Sin unidad específica'}</span>
                <span className="text-xs text-mist" data-id="element-172" data-mp="esnzkq2kr8y3">
                  {channelLabels[l.channel]}
                  <span className="block" data-id="element-173" data-mp="a1fprsxc1lke">{formatDateTime(l.createdAt)}</span>
                </span>
                <span className={`w-fit rounded-full border px-2.5 py-1 text-xs font-semibold ${statusColor[l.status]}`} data-id="element-174" data-mp="52w1vmz6hzpe">{statusLabel(l.status)}</span>
              </button>
            </li>)}
        </ul>}

      <Modal open={!!open} onClose={() => setOpenId(null)} title={`Consulta ${open?.code ?? ''}`} data-id="element-175" data-mp="3s72gp5g2l644">
        {open && <LeadDetail key={open.id + open.timeline.length} lead={open} onClose={() => setOpenId(null)} data-id="element-176" data-mp="3n9af1jpmi5mq" />}
      </Modal>
    </div>;
}